import { useTranslation } from "react-i18next";
import i18n from "i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import { testIds } from "@/testIds";
import styles from "./MobilePreview.module.scss";
import { MAX_BUSY_RETRIES, busyRetryAfter, waitFor } from "@/components/editor/preview/compilePlace";

const M = testIds.mobile;

type Fetch = (url: string, init?: RequestInit) => Promise<Response>;

export type PreviewState = "rebuilding" | "rebuilt" | "failed";

export interface MobilePreviewProps {
  fetchWithAuth: Fetch;
  /** The compiled PDF's address (the same /export/pdf the desktop preview uses). */
  pdfUrl: string;
  /** Changes to this redraw the PDF (a change Lilia made). */
  refreshKey?: number;
  /** "Ask Lilia to fix" when it couldn't rebuild. */
  onAskToFix?: (message: string) => void;
  /** "See issues" when it couldn't rebuild: Check document, listing what failed. */
  onSeeIssues?: () => void;
}

/**
 * Preview on a phone (Olivia, mobile reader 5): the real PDF pages, fit to the
 * width, pages scrolling, "Page n of N", the paper white in Dark. A phone
 * browser has no built-in PDF viewer, so the desktop preview's <iframe> shows
 * nothing there: the pages are drawn here with pdf.js (loaded only when Preview
 * opens). Pinch-zoom is the browser's own.
 */
export function MobilePreview({ fetchWithAuth, pdfUrl, refreshKey = 0, onAskToFix, onSeeIssues }: MobilePreviewProps) {
  const { t } = useTranslation("mobile");
  const [state, setState] = useState<PreviewState>("rebuilding");
  const [message, setMessage] = useState<string>("");
  const [pages, setPages] = useState(0);
  const [current, setCurrent] = useState(1);
  const pagesRef = useRef<HTMLDivElement>(null);

  const draw = useCallback(
    async (signal: AbortSignal) => {
      setState("rebuilding");
      setMessage("");
      try {
        // Every compiler busy for a minute: wait as the server says and ask again, as the desktop preview does.
        let res = await fetchWithAuth(pdfUrl, { signal });
        for (let attempt = 0; attempt < MAX_BUSY_RETRIES; attempt++) {
          const retryAfter = await busyRetryAfter(res);
          if (retryAfter === null) break;
          await waitFor(retryAfter * 1000, signal);
          res = await fetchWithAuth(pdfUrl, { signal });
        }
        if (!res.ok) {
          let text = i18n.t("mobile:preview.pdfFailed", { status: res.status });
          try {
            const body = (await res.json()) as { message?: string; error?: string };
            text = body.message ?? body.error ?? text;
          } catch {
            // Not JSON: the status says enough.
          }
          throw new Error(text);
        }
        const data = new Uint8Array(await res.arrayBuffer());
        const pdfjs = await import("pdfjs-dist");
        const worker = await import("pdfjs-dist/build/pdf.worker.min.mjs?url");
        // The build gives the worker's URL as a string; the dev server can hand back something else,
        // and pdf.js then stops with "Invalid workerSrc type" (phone-reader e2e, 16 Oct).
        pdfjs.GlobalWorkerOptions.workerSrc = typeof worker.default === "string"
          ? worker.default
          : new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();
        const doc = await pdfjs.getDocument({ data }).promise;
        if (signal.aborted) return;
        const host = pagesRef.current;
        if (!host) return;
        host.replaceChildren();
        const width = host.clientWidth || window.innerWidth;
        const ratio = Math.min(window.devicePixelRatio || 1, 2.5);
        for (let n = 1; n <= doc.numPages; n++) {
          const page = await doc.getPage(n);
          if (signal.aborted) return;
          const base = page.getViewport({ scale: 1 });
          const scale = (width - 24) / base.width;
          const viewport = page.getViewport({ scale: scale * ratio });
          const canvas = document.createElement("canvas");
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          canvas.style.width = `${Math.floor(viewport.width / ratio)}px`;
          canvas.style.height = `${Math.floor(viewport.height / ratio)}px`;
          canvas.className = styles.page;
          canvas.dataset.testid = M.previewPage;
          canvas.dataset.page = String(n);
          host.appendChild(canvas);
          const ctx = canvas.getContext("2d");
          if (ctx) await page.render({ canvas, canvasContext: ctx, viewport }).promise;
        }
        setPages(doc.numPages);
        setCurrent(1);
        setState("rebuilt");
      } catch (e) {
        if (signal.aborted) return;
        setMessage(e instanceof Error ? e.message : i18n.t("mobile:preview.pdfFailedPlain"));
        setState("failed");
      }
    },
    [fetchWithAuth, pdfUrl],
  );

  useEffect(() => {
    const ac = new AbortController();
    void draw(ac.signal);
    return () => ac.abort();
  }, [draw, refreshKey]);

  // "Page n of N": the page whose top is nearest the top of the view.
  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const top = e.currentTarget.getBoundingClientRect().top;
    const canvases = pagesRef.current?.querySelectorAll<HTMLCanvasElement>("canvas") ?? [];
    let best = 1;
    canvases.forEach((c, i) => {
      if (c.getBoundingClientRect().top - top < 120) best = i + 1;
    });
    setCurrent(best);
  };

  return (
    <div className={styles.preview}>
      <div className={styles.status} data-testid={M.previewStatus} data-state={state}>
        <span className={styles.dot} aria-hidden="true" />
        {state === "rebuilding" ? t("preview.rebuilding") : state === "rebuilt" ? t("preview.rebuilt") : t("preview.failed")}
        {state === "failed" && onSeeIssues && (
          <button type="button" className={styles.fix} data-testid={M.previewIssues} onClick={onSeeIssues}>
            {t("preview.seeIssues")}
          </button>
        )}
        {state === "failed" && onAskToFix && (
          <button type="button" className={styles.fix} onClick={() => onAskToFix(message)}>
            {t("preview.askFix")}
          </button>
        )}
      </div>
      {state === "failed" && <p className={styles.message}>{message}</p>}
      <div className={styles.scroller} onScroll={onScroll} data-state={state}>
        <div ref={pagesRef} className={styles.pages} />
      </div>
      {pages > 0 && (
        <div className={styles.counter} aria-live="polite">
          {t("preview.pageOf", { n: current, total: pages })}
        </div>
      )}
    </div>
  );
}
